<template>
  <div>
    <ReportMobileFrame
      v-if="$vuetify.breakpoint.smAndDown"
      :loading="isLoading"
      :empty="!isLoading && data.length === 0"
      empty-text="No usage data for the selected period."
    >
      <template #filters>
        <div class="report-dates-grid">
          <MobileDateField label="Start Date" :value="dates[0]" @input="onMobileDateChange(0, $event)" />
          <MobileDateField label="End Date" align="right" :value="dates[1] || dates[0]" @input="onMobileDateChange(1, $event)" />
        </div>
        <v-select
          v-if="storeOptions.length > 0"
          v-model="selectedStoreId"
          :items="storeOptions"
          item-text="name"
          item-value="id"
          label="Store"
          outlined dense hide-details
          clearable
          class="mt-2"
          @change="load"
        />
      </template>

      <div v-for="(category, ci) in data" :key="ci">
        <ReportGroupHeader :label="category.categoryName">
          <template #stats>
            <div class="stat">
              <span class="stat__label">Qty</span>
              <span class="stat__value">{{ category.categoryTotalQty }}</span>
            </div>
            <div class="stat">
              <span class="stat__label">Revenue</span>
              <span class="stat__value">£{{ category.categoryTotalRevenue.toFixed(2) }}</span>
            </div>
          </template>
        </ReportGroupHeader>
        <ReportListRow v-for="product in category.products" :key="product.productId">
          <template #title>{{ product.productName }}</template>
          <template #subtitle>
            Qty {{ product.totalQty }}
            <span v-if="product.isVolume"> · {{ formatMl(product.totalVolumeMl) }} · {{ product.bottlesConsumed }} bottles</span>
          </template>
          <template #trailing>£{{ product.totalRevenue.toFixed(2) }}</template>
        </ReportListRow>
      </div>

      <template #action>
        <v-btn block color="success" :loading="isLoading" x-large @click="exportExcel">
          <v-icon left>mdi-file-export-outline</v-icon>
          EXPORT REPORT
        </v-btn>
      </template>
    </ReportMobileFrame>

    <template v-else>
    <v-row>
      <v-toolbar flat class="toolbar-container mb-2" height='100'>
        <v-col>
          <div class="toolbar-datepicker">
            <DateRangePicker :initialDates="dates" @blur="updateDates" />
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="selectedStoreId"
            :items="storeOptions"
            item-text="name"
            item-value="id"
            label="Store"
            outlined
            dense
            hide-details
            clearable
          />
        </v-col>
      </v-toolbar>
    </v-row>
    <v-row>
      <v-col>
        <v-btn block class="search-btn" type="button" color="success" v-on:click="load">
          SEARCH
        </v-btn>
      </v-col>
      <v-col>
        <v-btn block type="button" color="primary" v-on:click="exportExcel()">
          EXPORT
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="isLoading">
      <v-col class="text-center">
        <v-progress-circular indeterminate color="primary" />
      </v-col>
    </v-row>

    <v-row v-else-if="data.length === 0">
      <v-col class="text-center text-muted mt-4">
        No usage data for the selected period.
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12">
        <v-expansion-panels multiple v-model="openCategories">
          <v-expansion-panel v-for="(category, ci) in data" :key="ci">
            <v-expansion-panel-header>
              <div class="category-header">
                <strong>{{ category.categoryName }}</strong>
                <span class="category-totals">
                  Qty: {{ category.categoryTotalQty }}
                  &middot; Volume: {{ formatMl(category.categoryTotalVolumeMl) }}
                  &middot; Revenue: £{{ category.categoryTotalRevenue.toFixed(2) }}
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="product in category.products" :key="product.productId" class="product-row">
                <div class="product-row__title">
                  <strong>{{ product.productName }}</strong>
                  <span v-if="product.isVolume" class="badge">Volume</span>
                </div>
                <div class="product-row__stats">
                  <span>Total Qty: <strong>{{ product.totalQty }}</strong></span>
                  <span v-if="product.isVolume">Total Volume: <strong>{{ formatMl(product.totalVolumeMl) }}</strong></span>
                  <span v-if="product.isVolume">Bottles Consumed: <strong>{{ product.bottlesConsumed }}</strong></span>
                  <span>Revenue: <strong>£{{ product.totalRevenue.toFixed(2) }}</strong></span>
                </div>
                <div class="product-row__sizes" v-if="product.sizeBreakdown && product.sizeBreakdown.length">
                  <v-chip
                    v-for="(size, si) in product.sizeBreakdown"
                    :key="si"
                    small
                    class="size-chip"
                    color="primary"
                    outlined
                  >
                    {{ size.sizeName }}<span v-if="size.volumeConsumedMl > 0"> ({{ size.volumeConsumedMl }}ml)</span>: {{ size.qtySold }}
                  </v-chip>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    </template>
  </div>
</template>

<script>
import DateRangePicker from '@/components/DateRangePicker.vue';
import ReportMobileFrame from '@/components/reports/ReportMobileFrame.vue';
import ReportGroupHeader from '@/components/reports/ReportGroupHeader.vue';
import ReportListRow from '@/components/reports/ReportListRow.vue';
import MobileDateField from '@/components/reports/MobileDateField.vue';
import { reportService, userService } from '@/services';
import { b64toBlob } from '@/helpers';
import { saveAs } from 'file-saver';
import moment from 'moment';
import router from '../../router';

export default {
  name: 'StockUsage',
  components: {
    DateRangePicker,
    ReportMobileFrame,
    ReportGroupHeader,
    ReportListRow,
    MobileDateField
  },
  data: () => ({
    dates: [moment().add(-7, 'd').toISOString().substr(0, 10), moment().toISOString().substr(0, 10)],
    selectedStoreId: null,
    storeOptions: [],
    data: [],
    isLoading: false,
    openCategories: []
  }),
  async created() {
    if (userService.isStaff()) {
      router.push('/products');
      return;
    }

    try {
      this.storeOptions = await reportService.getStores();
    } catch (e) {
      this.storeOptions = [];
    }

    await this.load();
  },
  methods: {
    updateDates(newDates) {
      this.dates = newDates;
    },
    formatMl(value) {
      if (!value) return '0ml';
      return (+value).toFixed(0) + 'ml';
    },
    async load() {
      const startDate = this.dates[0];
      const endDate = this.dates.length > 1 ? this.dates[1] : this.dates[0];

      this.isLoading = true;
      try {
        this.data = await reportService.getStockUsageReport(startDate, endDate, this.selectedStoreId);
        this.openCategories = this.data.map((_, i) => i);
      } finally {
        this.isLoading = false;
      }
    },
    async exportExcel() {
      const startDate = this.dates[0];
      const endDate = this.dates.length > 1 ? this.dates[1] : this.dates[0];

      this.isLoading = true;
      try {
        const response = await reportService.exportStockUsageReport(startDate, endDate, this.selectedStoreId);
        if (response == null) {
          alert('Error exporting');
          return;
        }
        const blob = b64toBlob(response, 'application/vnd.ms-excel');
        saveAs(blob, 'StockUsage.xls');
      } finally {
        this.isLoading = false;
      }
    },
    onMobileDateChange(index, value) {
      if (!value) return;
      const next = this.dates.slice();
      while (next.length <= index) next.push(value);
      next[index] = value;
      if (index === 0) next[1] = value;
      this.dates = next;
      this.load();
    }
  }
};
</script>

<style lang="scss" scoped>
.toolbar-container {
  background: #FFF !important;
  padding: 10px;
  height: auto;
  border-radius: 8px;
  border: 1px solid lighten(#7B5CD8, 30);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.category-totals {
  color: #64748b;
  font-size: 13px;
}

.product-row {
  border-bottom: 1px solid #eef1f5;
  padding: 10px 0;
}

.product-row:last-child {
  border-bottom: 0;
}

.product-row__title {
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.badge {
  background: #e0f2fe;
  border-radius: 4px;
  color: #0369a1;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  text-transform: uppercase;
}

.product-row__stats {
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  font-size: 13px;
  margin-bottom: 6px;
}

.product-row__sizes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.size-chip {
  font-size: 12px;
}
</style>
